import { selectIsAudioOnlyMode } from 'features/application/applicationSlice';
import { selectMaximizedStream } from 'features/layout/features/content/contentSlice';
import { PaginationUpdatePayload } from 'features/layout/features/pagination/types';
import {
  flushMediaUpdates,
  isLocalFeed,
  isPendingScreenshareSubscription,
  isScreenshareFeed,
  pauseVideoStream,
  pauseVideoStreams,
} from 'features/layout/pagination/mediaUpdates';
import { SourceDetails } from 'features/layout/types';
import { FeedId, SubscribeStreamList } from 'features/streaming/types';
import { sourceMatches } from 'features/streaming/utils';
import invert from 'lodash.invert';
import { store } from 'store/store';
import { canExposeInternals } from 'utils/development';
import { logger } from 'utils/logger';
import { ReceivingFeedStreamSendingState, RTCClient, ToggleMediaTemplate } from 'utils/webrtc';
import type { StreamMedia } from 'utils/webrtc/receiving/StreamMedia';

const isOffPageFeed = (feedId: FeedId, pageStreams: SourceDetails[]): boolean => {
  if (isScreenshareFeed(feedId)) {
    return false;
  }

  const feedUserId = RTCClient.userIdByFeedId[feedId];

  if (feedUserId === undefined) {
    return false;
  }

  return !pageStreams.some(
    ({ userId, kind }) => userId === feedUserId && (kind === 'remote' || kind === 'local')
  );
};

export const updatePagination = async (pageConfig: PaginationUpdatePayload) => {
  const state = store.getState();
  const audioOnly = selectIsAudioOnlyMode(state);

  const streamsToConfigure: ToggleMediaTemplate = [];
  const pausedStreams: StreamMedia[] = [];

  // Audio-only mode: stop pulling every participant camera feed regardless of page or maximize.
  if (audioOnly) {
    const cameraStreams: StreamMedia[] = [];

    RTCClient.receivingFeed.media.forEachFeed((list, feedId) => {
      if (isScreenshareFeed(feedId)) {
        return;
      }

      const videoStream = list.getVideoStream();
      if (videoStream) {
        cameraStreams.push(videoStream);
      }
    });

    await pauseVideoStreams(cameraStreams);
    return;
  }

  const pageStreams = [...pageConfig.streams];

  const feedIdByUserId = invert(RTCClient.userIdByFeedId);

  const streamsToSubscribe: SubscribeStreamList = [];

  const maximizedStream = selectMaximizedStream(state);

  if (maximizedStream && !pageStreams.find((stream) => sourceMatches(maximizedStream, stream))) {
    pageStreams.push(maximizedStream);
  }

  const pageStreamsByFeedId: Record<FeedId, SourceDetails> = {};
  for (const rec of pageStreams) {
    const feedId = rec.feedId || feedIdByUserId[rec.userId];
    if (feedId && !isLocalFeed(feedId)) {
      pageStreamsByFeedId[feedId] = rec;
    }
  }

  RTCClient.receivingFeed.media.forEachFeed((list, feedId) => {
    if (isLocalFeed(feedId)) {
      return;
    }

    const requestedStream = pageStreamsByFeedId[feedId];

    const videoStream = list.getVideoStream();
    // we got to subscribe/enable given stream
    if (requestedStream) {
      if (videoStream) {
        // TODO: Could also be improved;
        const canConfigure = videoStream.subscriberMid;

        if (canConfigure) {
          videoStream.setReceivingState(ReceivingFeedStreamSendingState.on);

          // we have a connection but it's currently not sending anything
          streamsToConfigure.push({
            mid: videoStream.subscriberMid,
            send: true,
          });
        } else {
          // handle case when we aren't subscribed to a specific video stream at all but know it exists
          streamsToSubscribe.push({
            feed: feedId,
            mid: videoStream.publisherMid,
          });
        }
      }
    }
    // stream isn't on the current page — stop receiving its video to save bandwidth
    else if (videoStream && isOffPageFeed(feedId, pageStreams)) {
      pauseVideoStream(videoStream, streamsToConfigure, pausedStreams);
    }

    delete pageStreamsByFeedId[feedId];
  });

  const unresolvedStreams = Object.entries(pageStreamsByFeedId).filter(
    ([feedId]) => !isPendingScreenshareSubscription(feedId)
  );

  if (unresolvedStreams.length) {
    logger
      .remote({ capture: 'layout' })
      .warn('unprocessed streams in pagination', Object.fromEntries(unresolvedStreams));
  }

  if (canExposeInternals()) {
    logger.warn(
      'conf',
      streamsToConfigure,
      'sub',
      streamsToSubscribe,
      'page',
      pageStreams,
      'page unprocessed',
      pageStreamsByFeedId
    );
  }

  await flushMediaUpdates(streamsToConfigure, pausedStreams, streamsToSubscribe);
};
